import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SecurityStatusDashboard from './components/SecurityStatusDashboard';
import ThreatDetectionPanel from './components/ThreatDetectionPanel';
import AccessControlMonitor from './components/AccessControlMonitor';
import AuditTrailAnalysis from './components/AuditTrailAnalysis';
import BackupRecoveryMonitor from './components/BackupRecoveryMonitor';
import ComplianceMonitoringPanel from './components/ComplianceMonitoringPanel';
import { auditTrailService } from '../../services/auditTrailService';
import { infrastructureMonitoringService } from '../../services/infrastructureMonitoringService';

const DataSecurityAuditTrailMonitor = () => {
  const [activeTab, setActiveTab] = useState('threat_detection');
  const [securityMetrics, setSecurityMetrics] = useState({
    activeThreats: 0,
    accessViolations: 0,
    auditLogVolume: 0,
    complianceScore: 100
  });
  const [threatAlerts, setThreatAlerts] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [auditEntries, setAuditEntries] = useState([]);
  const [backupStatus, setBackupStatus] = useState(null);
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filters, setFilters] = useState({
    severity: 'all',
    userRole: 'all',
    actionType: 'all',
    dataClassification: 'all'
  });

  useEffect(() => {
    loadSecurityData();

    // Real-time subscription for security alerts
    const alertChannel = infrastructureMonitoringService?.subscribeToAlerts((alert) => {
      if (alert?.alert_type === 'security_threat' || alert?.alert_type === 'access_violation') {
        setThreatAlerts(prev => [alert, ...prev]);
        updateSecurityMetrics();
      }
    });

    return () => {
      infrastructureMonitoringService?.unsubscribeFromAlerts(alertChannel);
    };
  }, [timeRange, filters]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadSecurityData();
      }, 30000); // Refresh every 30 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, timeRange, filters]);

  const loadSecurityData = async () => {
    try {
      setLoading(true);
      setError('');

      const [threats, access, audit, backup, compliance] = await Promise.all([
        loadThreatDetectionData(),
        loadAccessControlData(),
        loadAuditTrailData(),
        loadBackupRecoveryData(),
        loadComplianceData()
      ]);

      setThreatAlerts(threats);
      setAccessLogs(access);
      setAuditEntries(audit);
      setBackupStatus(backup);
      setComplianceStatus(compliance);

      updateSecurityMetrics();
    } catch (err) {
      setError(err?.message || 'Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const loadThreatDetectionData = async () => {
    try {
      const alerts = await infrastructureMonitoringService?.getInfrastructureAlerts({
        alertStatus: 'active',
        limit: 50
      });

      // Filter for security-related threats
      const securityThreats = alerts?.filter(alert => 
        alert?.alertType?.includes('security') || 
        alert?.alertType?.includes('unauthorized') ||
        alert?.alertType?.includes('breach')
      ) || [];

      return securityThreats;
    } catch (error) {
      console.error('Failed to load threat detection data:', error);
      return [];
    }
  };

  const loadAccessControlData = async () => {
    try {
      const auditData = await auditTrailService?.getAuditLogs({
        actionType: filters?.actionType !== 'all' ? filters?.actionType : undefined,
        dateFrom: getDateFromTimeRange(timeRange),
        limit: 100
      });

      // Filter for access-related actions
      const accessActions = auditData?.filter(log => 
        log?.action?.includes('access') || 
        log?.action?.includes('login') ||
        log?.action?.includes('permission')
      ) || [];

      return accessActions;
    } catch (error) {
      console.error('Failed to load access control data:', error);
      return [];
    }
  };

  const loadAuditTrailData = async () => {
    try {
      const result = await auditTrailService?.getAllAuditEntries({
        dateFrom: getDateFromTimeRange(timeRange),
        limit: 100
      });

      return result?.entries || [];
    } catch (error) {
      console.error('Failed to load audit trail data:', error);
      return [];
    }
  };

  const loadBackupRecoveryData = async () => {
    // Mock backup/recovery data - in production, this would come from cloud storage service
    return {
      lastBackup: new Date(Date.now() - 2 * 60 * 60 * 1000)?.toISOString(),
      backupStatus: 'completed',
      replicationStatus: 'active',
      recoveryTestDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString(),
      recoveryTestResult: 'passed',
      dataIntegrity: 100,
      storageUsed: 245.8,
      storageLimit: 500
    };
  };

  const loadComplianceData = async () => {
    // Mock PNG labor law compliance data
    return {
      dataRetentionCompliance: 100,
      privacyPolicyCompliance: 100,
      accessControlCompliance: 98,
      encryptionCompliance: 100,
      auditLogCompliance: 100,
      overallScore: 99.6,
      violations: [],
      lastAudit: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()
    };
  };

  const updateSecurityMetrics = () => {
    setSecurityMetrics({
      activeThreats: threatAlerts?.filter(t => t?.alertStatus === 'active')?.length || 0,
      accessViolations: accessLogs?.filter(l => l?.action?.includes('denied') || l?.action?.includes('failed'))?.length || 0,
      auditLogVolume: auditEntries?.length || 0,
      complianceScore: complianceStatus?.overallScore || 100
    });
  };

  const getDateFromTimeRange = (range) => {
    const timeRanges = {
      '1h': 1,
      '6h': 6,
      '24h': 24,
      '7d': 168,
      '30d': 720
    };
    const hours = timeRanges?.[range] || 24;
    return new Date(Date.now() - hours * 60 * 60 * 1000)?.toISOString();
  };

  const handleExportReport = async (reportType) => {
    try {
      let exportData = {};
      let filename = '';

      switch (reportType) {
        case 'security_summary':
          exportData = {
            metrics: securityMetrics,
            threats: threatAlerts,
            generatedAt: new Date()?.toISOString()
          };
          filename = `security_summary_${Date.now()}.json`;
          break;
        case 'audit_trail':
          exportData = {
            entries: auditEntries,
            totalEntries: auditEntries?.length,
            timeRange,
            generatedAt: new Date()?.toISOString()
          };
          filename = `audit_trail_${Date.now()}.json`;
          break;
        case 'compliance_report':
          exportData = {
            complianceStatus,
            backupStatus,
            securityMetrics,
            generatedAt: new Date()?.toISOString()
          };
          filename = `compliance_report_${Date.now()}.json`;
          break;
        default:
          break;
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link?.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export report:', error);
    }
  };

  const tabs = [
    { id: 'threat_detection', label: 'Threat Detection', icon: 'AlertTriangle' },
    { id: 'access_control', label: 'Access Control', icon: 'Lock' },
    { id: 'audit_trail', label: 'Audit Trail', icon: 'FileText' },
    { id: 'backup_recovery', label: 'Backup & Recovery', icon: 'Database' },
    { id: 'compliance', label: 'Compliance', icon: 'Shield' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Data Security & Audit Trail Monitor | PNG HR Analytics</title>
        <meta name="description" content="Comprehensive security oversight with real-time threat detection, access logging, and compliance audit capabilities" />
      </Helmet>
      <Header />
      <ComplianceAlertBanner />
      <GlobalFilterBar onFilterChange={setFilters} />
      <DataRefreshIndicator isRefreshing={loading} lastRefresh={new Date()} />
      <main className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Page Header - Improved responsive layout */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-6">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3 flex-wrap">
                <Icon name="Shield" className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 flex-shrink-0" />
                <span className="break-words">Data Security & Audit Trail Monitor</span>
              </h1>
              <p className="mt-2 text-sm sm:text-base text-gray-600 leading-relaxed">
                Comprehensive security oversight with real-time threat detection, access logging, and compliance audit capabilities for PNG HR Analytics platform
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 flex-shrink-0">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e?.target?.value)}
                className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full sm:w-auto"
              >
                <option value="1h">Last Hour</option>
                <option value="6h">Last 6 Hours</option>
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
              </select>
              <Button
                onClick={() => setAutoRefresh(!autoRefresh)}
                variant={autoRefresh ? 'primary' : 'outline'}
                className="flex items-center gap-2 text-sm whitespace-nowrap"
              >
                <Icon name={autoRefresh ? 'RefreshCw' : 'Pause'} className="w-4 h-4" />
                <span className="hidden sm:inline">{autoRefresh ? 'Auto-Refresh On' : 'Auto-Refresh Off'}</span>
                <span className="sm:hidden">Auto</span>
              </Button>
              <Button
                onClick={() => handleExportReport('security_summary')}
                variant="outline"
                className="flex items-center gap-2 text-sm whitespace-nowrap"
              >
                <Icon name="Download" className="w-4 h-4" />
                <span className="hidden sm:inline">Export Report</span>
                <span className="sm:hidden">Export</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 text-sm sm:text-base break-words flex-1">{error}</p>
          </div>
        )}

        {/* Security Status Dashboard - Improved responsive grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <SecurityStatusDashboard
            activeThreats={securityMetrics?.activeThreats}
            accessViolations={securityMetrics?.accessViolations}
            auditLogVolume={securityMetrics?.auditLogVolume}
            complianceScore={securityMetrics?.complianceScore}
            loading={loading}
          />
        </div>

        {/* Tab Navigation - Improved responsive design */}
        <div className="mb-6 border-b border-gray-200 overflow-x-auto">
          <nav className="flex gap-2 sm:gap-6 min-w-max">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-3 border-b-2 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
                  activeTab === tab?.id
                    ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <Icon name={tab?.icon} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Monitoring Panel - Improved container */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {activeTab === 'threat_detection' && (
            <ThreatDetectionPanel
              threats={threatAlerts}
              loading={loading}
              onRefresh={loadSecurityData}
              filters={filters}
              onFilterChange={setFilters}
            />
          )}

          {activeTab === 'access_control' && (
            <AccessControlMonitor
              accessLogs={accessLogs}
              loading={loading}
              onRefresh={loadSecurityData}
              filters={filters}
              onFilterChange={setFilters}
            />
          )}

          {activeTab === 'audit_trail' && (
            <AuditTrailAnalysis
              auditEntries={auditEntries}
              loading={loading}
              onRefresh={loadSecurityData}
              onExport={() => handleExportReport('audit_trail')}
              filters={filters}
              onFilterChange={setFilters}
            />
          )}

          {activeTab === 'backup_recovery' && (
            <BackupRecoveryMonitor
              backupStatus={backupStatus}
              loading={loading}
              onRefresh={loadSecurityData}
            />
          )}

          {activeTab === 'compliance' && (
            <ComplianceMonitoringPanel
              complianceStatus={complianceStatus}
              loading={loading}
              onRefresh={loadSecurityData}
              onExport={() => handleExportReport('compliance_report')}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default DataSecurityAuditTrailMonitor;